/**
 * Line-level tax arithmetic, in integer minor units.
 *
 * Two computations, not one. Where a price already contains the tax — normal for retail in the
 * region — the tax has to be *extracted*; where it does not, it is *added*. Doing either with the
 * other's formula is wrong by roughly the tax rate, which is large enough to notice and small
 * enough to ship.
 */

export const BASIS_POINTS = 10_000;

/** Mirrors the TaxTreatment enum. Kept as strings so this module stays free of Prisma. */
export type TaxTreatmentCode = 'STANDARD' | 'ZERO_RATED' | 'EXEMPT';

export interface LineTaxInput {
  /** Unit price × quantity, before discount. Includes tax when `pricesIncludeTax`. */
  grossBeforeDiscountMinor: number;
  /** Already-computed discount, in the same tax basis as the gross. */
  discountMinor: number;
  rateBasisPoints: number;
  treatment: TaxTreatmentCode;
  pricesIncludeTax: boolean;
}

export interface LineTaxResult {
  /** After discount, excluding tax. */
  netMinor: number;
  taxMinor: number;
  /** What the customer pays for the line: net + tax. */
  totalMinor: number;
}

/** Half-up on a ratio of integers, without ever creating a float from money. */
function divideRoundHalfUp(numerator: number, denominator: number): number {
  const sign = numerator < 0 ? -1 : 1;
  const value = Math.abs(numerator);

  return sign * Math.floor((value * 2 + denominator) / (denominator * 2));
}

/**
 * Computes the net, tax and total of one line.
 *
 * Rounding happens once, here, and order totals are the sum of these already-rounded lines. The
 * alternative — rounding the order total — produces a receipt whose lines do not add up to their
 * own total, which is the complaint customers actually make.
 */
export function computeLineTax(input: LineTaxInput): LineTaxResult {
  const gross = input.grossBeforeDiscountMinor - input.discountMinor;

  // Exempt is outside the tax entirely; zero-rated is inside it at 0%. Both yield no tax, and the
  // difference is carried by the treatment on the line, not by the arithmetic.
  const taxable = input.treatment === 'STANDARD' && input.rateBasisPoints > 0;

  if (!taxable) {
    return { netMinor: gross, taxMinor: 0, totalMinor: gross };
  }

  if (input.pricesIncludeTax) {
    // Extract: the gross is what the customer pays, so the total must come back unchanged.
    const netMinor = divideRoundHalfUp(gross * BASIS_POINTS, BASIS_POINTS + input.rateBasisPoints);

    // Derived by subtraction rather than computed from the rate. Computing both independently
    // leaves net + tax disagreeing with gross by a unit on roughly a third of lines.
    return { netMinor, taxMinor: gross - netMinor, totalMinor: gross };
  }

  const taxMinor = divideRoundHalfUp(gross * input.rateBasisPoints, BASIS_POINTS);
  return { netMinor: gross, taxMinor, totalMinor: gross + taxMinor };
}

/**
 * A percentage discount on a line, rounded once.
 *
 * Percent is whole numbers; a fractional discount is expressed as an explicit amount instead of
 * inviting a float into the calculation.
 */
export function percentageDiscountMinor(grossMinor: number, percent: number): number {
  if (percent <= 0) return 0;
  return divideRoundHalfUp(grossMinor * percent, 100);
}

/**
 * Spreads an order-level discount across lines in proportion to their value.
 *
 * The remainder from rounding goes to the largest line, so the parts always sum to exactly the
 * discount that was granted — a shortfall of one unit is the kind of thing that turns into an
 * unexplainable penny on a reconciliation report.
 */
export function distributeDiscount(lineGrossMinor: number[], discountMinor: number): number[] {
  const total = lineGrossMinor.reduce((sum, value) => sum + value, 0);
  if (total <= 0 || discountMinor <= 0) return lineGrossMinor.map(() => 0);

  const shares = lineGrossMinor.map((gross) => divideRoundHalfUp(gross * discountMinor, total));
  const allocated = shares.reduce((sum, value) => sum + value, 0);
  const remainder = discountMinor - allocated;

  if (remainder !== 0) {
    let largest = 0;
    for (let index = 1; index < lineGrossMinor.length; index += 1) {
      if ((lineGrossMinor[index] ?? 0) > (lineGrossMinor[largest] ?? 0)) largest = index;
    }
    shares[largest] = (shares[largest] ?? 0) + remainder;
  }

  return shares;
}
